import { useCallback, useEffect, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { PageShell } from "@/components/mai/PageShell";
import { useProduct } from "@/context/ProductContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Flask, ArrowRight, ArrowUp, FloppyDisk, DownloadSimple, Trash, CurrencyCircleDollar, CircleNotch } from "@phosphor-icons/react";
import { CommentBadge, CommentThread } from "@/components/mai/Comments";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const Metric = ({ label, before, after }) => {
  const changed = before !== after;
  return (
    <div className="p-5 border border-slate-200 bg-white">
      <p className="overline-label text-slate-400">{label}</p>
      <p className="font-heading font-extrabold text-2xl mt-2 flex items-center gap-2">
        <span className={changed ? "text-slate-400 line-through decoration-2" : ""}>{before}</span>
        {changed && <><ArrowRight size={16} className="text-slate-400" /><span className="text-emerald-700">{after}</span></>}
      </p>
    </div>
  );
};

function SavedScenarios({ productId, refreshKey }) {
  const [saved, setSaved] = useState([]);
  const [busy, setBusy] = useState(null);
  const [comments, setComments] = useState({});
  const [openThread, setOpenThread] = useState(null);

  const load = useCallback(() => {
    axios.get(`${API}/scenario/${productId}/saved`).then((r) => setSaved(r.data)).catch(console.error);
  }, [productId]);

  const loadComments = useCallback(() => {
    axios.get(`${API}/comments/${productId}/summary`, { params: { entity_type: "scenario" } })
      .then((r) => setComments(r.data)).catch(console.error);
  }, [productId]);

  useEffect(() => { load(); loadComments(); }, [load, loadComments, refreshKey]);

  const exportPdf = async (id) => {
    setBusy(id);
    try {
      const r = await axios.post(`${API}/scenario/${productId}/saved/${id}/export`);
      toast.success("One-page summary ready — downloading");
      window.open(`${process.env.REACT_APP_BACKEND_URL}${r.data.download_url}`, "_blank");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Export failed");
    } finally { setBusy(null); }
  };

  const remove = async (id) => {
    await axios.delete(`${API}/scenario/${productId}/saved/${id}`);
    toast.success("Saved scenario deleted");
    load();
  };

  if (saved.length === 0) return null;
  return (
    <section data-testid="saved-scenarios" className="bg-white border border-slate-200">
      <div className="px-5 py-4 border-b border-slate-200">
        <p className="font-heading font-bold text-sm tracking-tight">Saved Scenarios</p>
        <p className="text-xs text-slate-500 mt-1">Named business cases persisted for this product — export any as a one-page PDF for a budget proposal.</p>
      </div>
      {saved.map((s) => (
        <div key={s.id} data-testid={`saved-scenario-${s.id}`} className="px-5 py-3.5 border-b border-slate-100 last:border-0">
          <div className="flex items-center gap-4">
            <div className="flex-1 min-w-0">
              <p className="text-xs font-semibold text-slate-800">{s.name}
                <span className="ml-2 inline-block align-middle"><CommentBadge summary={comments[s.id]} entityKey={s.id} onClick={() => setOpenThread(openThread === s.id ? null : s.id)} /></span>
              </p>
              <p className="text-[11px] text-slate-500 mt-0.5">{s.result.headline}</p>
              <p className="font-mono-data text-[10px] text-slate-400 mt-0.5">
                {new Date(s.created_at).toLocaleString()} · {s.created_by} · {s.toggled_investments.length} investment(s): {s.toggled_investments.slice(0, 3).join("; ")}{s.toggled_investments.length > 3 ? " …" : ""}
              </p>
            </div>
            <button data-testid={`scenario-export-${s.id}`} disabled={!!busy} onClick={() => exportPdf(s.id)}
              className="text-[11px] font-semibold text-slate-700 border border-slate-300 px-3 py-2 hover:bg-slate-50 flex items-center gap-1.5 disabled:opacity-50">
              {busy === s.id ? <CircleNotch size={12} className="animate-spin" /> : <DownloadSimple size={12} />} PDF
            </button>
            <button data-testid={`scenario-delete-${s.id}`} onClick={() => remove(s.id)} className="text-slate-300 hover:text-red-600 p-1.5"><Trash size={14} /></button>
          </div>
          {openThread === s.id && <CommentThread productId={productId} entityType="scenario" entityKey={s.id} onChanged={loadComments} />}
        </div>
      ))}
    </section>
  );
}

function PricingCorridor({ productId }) {
  const [config, setConfig] = useState(null);
  const [anchor, setAnchor] = useState("");
  const [price, setPrice] = useState("");
  const [currency, setCurrency] = useState("USD");
  const [result, setResult] = useState(null);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    setConfig(null); setResult(null); setAnchor("");
    axios.get(`${API}/pricing/${productId}/corridor-config`).then((r) => {
      setConfig(r.data);
      if (r.data.anchors.length > 0) setAnchor(r.data.anchors[0].id);
    }).catch(console.error);
  }, [productId]);

  const run = async () => {
    if (!price || Number(price) <= 0) return toast.error("Enter a positive launch price");
    setBusy(true);
    try {
      const r = await axios.post(`${API}/pricing/${productId}/corridor`, { anchor_id: anchor, price: Number(price), currency });
      setResult(r.data);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Simulation failed");
    } finally { setBusy(false); }
  };

  if (!config) return null;
  return (
    <section data-testid="pricing-corridor" className="bg-white border border-slate-200">
      <div className="px-5 py-4 border-b border-slate-200 flex items-center gap-3">
        <CurrencyCircleDollar size={20} className="text-slate-400" />
        <div>
          <p className="font-heading font-bold text-sm tracking-tight">Pricing Corridor Impact</p>
          <p className="text-xs text-slate-500 mt-1">Set a proposed launch price in an anchor market and see the indicative referenced price range everywhere that market's price is referenced.</p>
        </div>
      </div>
      <div className="px-5 py-4 flex flex-wrap items-center gap-2 border-b border-slate-100 bg-slate-50/60">
        <select data-testid="corridor-anchor-select" value={anchor} onChange={(e) => { setAnchor(e.target.value); setResult(null); }}
          className="text-xs border border-slate-300 bg-white px-3 py-2">
          {config.anchors.map((a) => <option key={a.id} value={a.id}>{a.flag} {a.name}</option>)}
        </select>
        <Input data-testid="corridor-price-input" type="number" min="0" placeholder="Proposed launch price, e.g. 25000"
          value={price} onChange={(e) => setPrice(e.target.value)} className="h-9 w-56 text-xs bg-white" />
        <Input data-testid="corridor-currency-input" value={currency} onChange={(e) => setCurrency(e.target.value)} className="h-9 w-20 text-xs bg-white" />
        <Button data-testid="corridor-run-btn" onClick={run} disabled={busy || !anchor}
          className="bg-slate-900 text-white hover:bg-slate-700 rounded-none gap-2 h-9">
          {busy ? <CircleNotch size={14} className="animate-spin" /> : <Flask size={14} weight="bold" />} Calculate corridor
        </Button>
      </div>
      {result && (
        <>
          <p data-testid="corridor-headline" className="px-5 py-3 text-xs font-semibold text-slate-700 border-b border-slate-100">{result.headline}</p>
          <div className="overflow-x-auto">
            <table className="w-full text-xs">
              <thead><tr className="border-b border-slate-200 text-left">
                {["Market", "Reference Basis", "Estimated Referenced Price Range"].map((h) => <th key={h} className="px-5 py-3 overline-label text-slate-400 font-semibold">{h}</th>)}
              </tr></thead>
              <tbody className="divide-y divide-slate-100">
                {result.rows.map((r) => (
                  <tr key={r.id} data-testid={`corridor-row-${r.id}`}>
                    <td className="px-5 py-2.5 font-semibold whitespace-nowrap align-top">{r.flag} {r.market}</td>
                    <td className="px-5 py-2.5 text-slate-500 align-top">{r.reference_basis} <span className="font-mono-data text-[10px] text-slate-400">(×{r.factor_low}–{r.factor_high})</span></td>
                    <td className="px-5 py-2.5 font-mono-data font-bold text-slate-800 whitespace-nowrap align-top">{r.range_label}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          {result.unaffected.length > 0 && (
            <p className="px-5 py-3 text-[11px] text-slate-400 border-t border-slate-100">
              Not directly affected: {result.unaffected.map((u) => u.market).join(", ")} — their reference baskets do not include {result.anchor.name}.
            </p>
          )}
          <p data-testid="corridor-disclaimer" className="px-5 py-3 text-[11px] italic text-amber-700 bg-amber-50/60 border-t border-amber-100">{result.disclaimer}</p>
        </>
      )}
    </section>
  );
}

export default function ScenarioPage() {
  const { productId } = useProduct();
  const [sugg, setSugg] = useState(null);
  const [selected, setSelected] = useState(new Set());
  const [result, setResult] = useState(null);
  const [running, setRunning] = useState(false);
  const [scenarioName, setScenarioName] = useState("");
  const [saving, setSaving] = useState(false);
  const [refreshKey, setRefreshKey] = useState(0);

  useEffect(() => {
    setSugg(null); setSelected(new Set()); setResult(null); setScenarioName("");
    axios.get(`${API}/scenario/${productId}/suggestions`).then((r) => setSugg(r.data)).catch(console.error);
  }, [productId]);

  const toggle = (id) => {
    const next = new Set(selected);
    next.has(id) ? next.delete(id) : next.add(id);
    setSelected(next);
    setResult(null);
  };

  const run = async () => {
    setRunning(true);
    try {
      const specs = sugg.suggestions.filter((s) => selected.has(s.id)).map((s) => s.spec);
      const r = await axios.post(`${API}/scenario/${productId}/simulate`, { add_assets: specs });
      setResult(r.data);
    } catch (e) { console.error(e); }
    setRunning(false);
  };

  const save = async () => {
    if (!scenarioName.trim()) return toast.error("Give the scenario a name first");
    setSaving(true);
    try {
      const chosen = sugg.suggestions.filter((s) => selected.has(s.id));
      await axios.post(`${API}/scenario/${productId}/save`, {
        name: scenarioName,
        add_assets: chosen.map((s) => s.spec),
        toggled_names: chosen.map((s) => s.name),
      });
      toast.success("Scenario saved — find it under Saved Scenarios");
      setScenarioName("");
      setRefreshKey((k) => k + 1);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Save failed");
    } finally { setSaving(false); }
  };

  return (
    <PageShell pageId="whatif" overline="What-if Scenario Engine · Nothing Persisted Unless Saved" title="Scenario Simulator">
      {!sugg ? (
        <div data-testid="loading-state" className="py-32 text-center text-slate-400 text-sm">Deriving proposed studies from the Gap Engine…</div>
      ) : (
        <div className="space-y-6">
          <section className="bg-white border border-slate-200">
            <div className="px-5 py-4 border-b border-slate-200 flex items-center justify-between">
              <div>
                <p className="font-heading font-bold text-sm tracking-tight">Proposed Evidence Investments</p>
                <p className="text-xs text-slate-500 mt-1">Toggle studies on, then simulate — waves, gaps and readiness reorganize live. {sugg.note}</p>
              </div>
              <Button data-testid="run-simulation-btn" onClick={run} disabled={selected.size === 0 || running}
                className="bg-slate-900 text-white hover:bg-slate-700 rounded-none gap-2">
                <Flask size={16} weight="bold" /> {running ? "Simulating…" : `Simulate (${selected.size})`}
              </Button>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 divide-y md:divide-y-0 divide-slate-100">
              {sugg.suggestions.map((s) => (
                <button key={s.id} data-testid={`scenario-toggle-${s.id}`} onClick={() => toggle(s.id)}
                  className={`text-left px-5 py-4 border-b border-r border-slate-100 transition-colors ${selected.has(s.id) ? "bg-slate-900 text-white" : "hover:bg-slate-50"}`}>
                  <div className="flex items-center justify-between gap-2">
                    <p className="text-sm font-semibold">{s.name}</p>
                    <span className={`shrink-0 text-[9px] uppercase tracking-wider px-1.5 py-0.5 border ${selected.has(s.id) ? "border-white/40" : "border-slate-200 text-slate-400"}`}>
                      {selected.has(s.id) ? "ON" : "OFF"}
                    </span>
                  </div>
                  <p className={`text-xs mt-1 leading-relaxed ${selected.has(s.id) ? "text-white/70" : "text-slate-500"}`}>{s.rationale}</p>
                  <p className={`font-mono-data text-[10px] mt-2 ${selected.has(s.id) ? "text-white/60" : "text-slate-400"}`}>
                    {s.markets_addressed.length} market(s): {s.markets_addressed.slice(0, 5).map((m) => m.flag).join(" ")}{s.markets_addressed.length > 5 ? " …" : ""}
                  </p>
                </button>
              ))}
            </div>
          </section>

          {result && (
            <>
              <div data-testid="scenario-headline" className="bg-slate-900 text-white px-6 py-4 flex flex-wrap items-center gap-4">
                <div className="flex-1 min-w-0">
                  <p className="overline-label text-white/50">Simulation Result</p>
                  <p className="font-heading font-bold text-lg mt-1">{result.headline}</p>
                </div>
                <div className="flex items-center gap-2">
                  <Input data-testid="scenario-name-input" value={scenarioName} onChange={(e) => setScenarioName(e.target.value)}
                    placeholder="Name this scenario…" className="h-9 w-56 text-xs bg-white/10 border-white/25 text-white placeholder:text-white/40" />
                  <Button data-testid="save-scenario-btn" onClick={save} disabled={saving}
                    className="bg-white text-slate-900 hover:bg-slate-100 rounded-none gap-2 h-9">
                    {saving ? <CircleNotch size={14} className="animate-spin" /> : <FloppyDisk size={14} weight="bold" />} Save Scenario
                  </Button>
                </div>
              </div>
              <div data-testid="scenario-metrics" className="grid grid-cols-2 md:grid-cols-3 xl:grid-cols-6 gap-4">
                <Metric label="Wave 1 — Submit Now" before={result.before.waves.wave_1} after={result.after.waves.wave_1} />
                <Metric label="Wave 2 — Fast Follow" before={result.before.waves.wave_2} after={result.after.waves.wave_2} />
                <Metric label="Wave 3 — Evidence First" before={result.before.waves.wave_3} after={result.after.waves.wave_3} />
                <Metric label="Avg Readiness" before={result.before.avg_readiness} after={result.after.avg_readiness} />
                <Metric label="Coverage Rate %" before={result.before.coverage_rate} after={result.after.coverage_rate} />
                <Metric label="Total Gaps" before={result.before.total_gaps} after={result.after.total_gaps} />
              </div>
              <section data-testid="scenario-impact" className="bg-white border border-slate-200">
                <div className="px-5 py-4 border-b border-slate-200">
                  <p className="font-heading font-bold text-sm tracking-tight">Market-Level Impact</p>
                </div>
                <div className="overflow-x-auto">
                  <table className="w-full text-xs">
                    <thead><tr className="border-b border-slate-200 text-left">
                      {["Market", "Wave", "Readiness", "Gaps"].map((h) => <th key={h} className="px-5 py-3 overline-label text-slate-400 font-semibold">{h}</th>)}
                    </tr></thead>
                    <tbody className="divide-y divide-slate-100">
                      {result.country_impact.map((c) => (
                        <tr key={c.id} data-testid={`impact-row-${c.id}`} className={c.wave_improved || c.readiness_delta > 0 ? "bg-emerald-50/50" : ""}>
                          <td className="px-5 py-2.5 font-semibold whitespace-nowrap">{c.flag} {c.name}</td>
                          <td className="px-5 py-2.5">
                            {c.wave_before !== c.wave_after
                              ? <span className="text-emerald-700 font-bold flex items-center gap-1"><ArrowUp size={12} weight="bold" /> Wave {c.wave_before} → {c.wave_after}</span>
                              : <span className="text-slate-500">Wave {c.wave_before}</span>}
                          </td>
                          <td className="px-5 py-2.5 font-mono-data">
                            {c.readiness_before}{c.readiness_delta !== 0 && <span className={c.readiness_delta > 0 ? "text-emerald-700 font-bold" : "text-red-600"}> → {c.readiness_after} ({c.readiness_delta > 0 ? "+" : ""}{c.readiness_delta})</span>}
                          </td>
                          <td className="px-5 py-2.5 font-mono-data">
                            {c.gaps_before}{c.gaps_after !== c.gaps_before && <span className="text-emerald-700 font-bold"> → {c.gaps_after}</span>}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </section>
            </>
          )}

          <SavedScenarios productId={productId} refreshKey={refreshKey} />
          <PricingCorridor productId={productId} />
        </div>
      )}
    </PageShell>
  );
}
